import { Star } from "@phosphor-icons/react";
import clsx from "clsx";

import LinkedInAccountsTag from "common/components/LinkedInAccountsTag";
import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useSNConversation from "../../hooks/useSNConversation";
import { getGroupParticipantsString, stripTextHighlight } from "../../utils";
import ConversationDropdown from "../ConversationActions";
import GroupAvatar from "../GroupAvatar/GroupAvatar";
import HighlightedText from "../HighlightedText";
import MessageDetails from "./MessageDetails";
import SalesNavigatorTag from "./SalesNavigatorTag";
import TimestampDisplay from "./TimestampDisplay";

import type { ReactNode } from "react";
import type { Conversation } from "../../types";

interface ConversationItemProps {
  conversation: Conversation;
  onSelect: () => void;
  isLoading: boolean;
  isSelected: boolean;
  isNextSelected: boolean;
}

export default function ConversationItem({
  conversation,
  onSelect,
  isLoading,
  isSelected,
  isNextSelected, // used to style borders based on next item selection
}: ConversationItemProps) {
  const {
    last_message: lastMessage,
    unread_count: unreadCount,
    owner: accountId,
    participants,
    highlight,
  } = conversation;
  const {
    full_name: fullName,
    picture_url: pictureUrl,
    is_lead: isLead,
    favorite: isFavorite,
  } = participants[0];

  const isDesktop = useTwBreakpoint("lg");
  const { isSNConversation, hasSNContract } = useSNConversation(conversation);

  const isMine = accountId === lastMessage.sender.id;
  const isGroup = participants.length > 1;

  const unreadCountComponent = (
    <span className="flex min-h-5 min-w-5 flex-col items-center justify-center rounded-full bg-purple-600 text-xxs-bold text-white">
      {unreadCount > 9 ? "9+" : unreadCount}
    </span>
  );

  let participantComponentContent: ReactNode = isGroup
    ? getGroupParticipantsString(participants)
    : fullName;

  if (
    highlight &&
    "full_name" in highlight &&
    stripTextHighlight(highlight.full_name[0]) === fullName
  ) {
    participantComponentContent = (
      <HighlightedText highlightClassname="text-xs-bold">
        {highlight.full_name[0]}
      </HighlightedText>
    );
  }

  return (
    /** biome-ignore lint/a11y/useSemanticElements: this "button" has to be a div since it contains a button element */
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        "flex flex-col gap-2 border-r-2 p-3 transition-colors duration-100 ease-linear border-b border-b-black-200 group-first:border-t group-last:mb-0 lg:px-4",
        isSelected
          ? "border-r-purple-500 bg-purple-50 group-first:border-t-transparent"
          : "border-r-transparent hover:bg-black-50 group-first:border-t-black-200",
        (isNextSelected || isSelected) && "border-b-transparent",
        isLoading && "cursor-not-allowed opacity-50",
      )}
      aria-label={`Conversation with ${fullName}`}
      onClick={() => {
        if (isLoading) return;
        onSelect();
      }}
      onKeyDown={(e) => {
        if (isLoading) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Participant details (name, avatar) */}
      <div className="flex items-start">
        <div className="flex items-center gap-1.5 flex-1">
          {isGroup ? (
            <GroupAvatar participants={participants} size="sm" />
          ) : (
            <ProfileImage
              className="cursor-pointer rounded-full"
              size="sm"
              src={pictureUrl}
            />
          )}
          <span className="line-clamp-1 text-xs-bold">
            {participantComponentContent}
          </span>

          <RenderIf condition={!isGroup && isLead && isFavorite}>
            <Star
              className="min-h-3.5 min-w-3.5 text-yellow-500 transition-colors duration-200"
              weight="fill"
              size={16}
            />
          </RenderIf>
        </div>
        <div className="flex items-start lg:items-center h-6.5">
          <RenderIf condition={isSNConversation}>
            <SalesNavigatorTag hasContract={hasSNContract} />
          </RenderIf>
          <div className="flex">
            <RenderIf condition={isDesktop && !!unreadCount}>
              <div className="m-0.5 group-hover:hidden ml-2.5">
                {unreadCountComponent}
              </div>
            </RenderIf>
            <div className="group-hover:flex lg:hidden">
              <ConversationDropdown conversation={conversation} />
            </div>
          </div>
        </div>
      </div>

      {/* Last message from the conversation */}
      <div className="flex flex-row justify-between gap-2">
        <MessageDetails conversation={conversation} isMine={isMine} />

        <RenderIf condition={!isDesktop && !!unreadCount}>
          {unreadCountComponent}
        </RenderIf>
      </div>

      {/* Timestamp details */}
      <div className="flex w-full flex-row items-center justify-between gap-8">
        <LinkedInAccountsTag accountIds={[accountId]} size="sm" />

        <TimestampDisplay
          createdAt={lastMessage.created_at}
          unreadCount={unreadCount}
        />
      </div>
    </div>
  );
}
